import { Injectable } from '@angular/core';
import { DcQuoteDetail } from '../data/models/DcProject';

/**
 * Stores the Daikin City project/quote context the user entered Altherma with in
 * the browser's **session storage** (cleared when the tab closes), under the
 * `daikin.projectId` / `daikin.projectName` / `daikin.quoteId` keys.
 *
 * Session storage (not local storage) so the linked quote lives only for the
 * current visit: entering from the tool section starts clean, while entering via
 * the parent app's "Add Products" redirect seeds it from the quote's details.
 */
@Injectable({ providedIn: 'root' })
export class QuoteSessionService {

  private static readonly PROJECT_ID = 'daikin.projectId';
  private static readonly PROJECT_NAME = 'daikin.projectName';
  private static readonly QUOTE_ID = 'daikin.quoteId';

  /** Persist the full project/quote context (projectId, projectName, quoteId). */
  setContext(context: DcQuoteDetail): void {
    this.setOrRemove(QuoteSessionService.PROJECT_ID, context.projectId);
    this.setOrRemove(QuoteSessionService.PROJECT_NAME, context.projectName);
    sessionStorage.setItem(QuoteSessionService.QUOTE_ID, context.quoteId);
  }

  setQuoteId(quoteId: string): void {
    sessionStorage.setItem(QuoteSessionService.QUOTE_ID, quoteId);
  }

  getQuoteId(): string | null {
    return sessionStorage.getItem(QuoteSessionService.QUOTE_ID);
  }

  getProjectId(): string | null {
    return sessionStorage.getItem(QuoteSessionService.PROJECT_ID);
  }

  getProjectName(): string | null {
    return sessionStorage.getItem(QuoteSessionService.PROJECT_NAME);
  }

  /** Remove every DC quote-context key (e.g. when entering from the tool section). */
  clear(): void {
    sessionStorage.removeItem(QuoteSessionService.PROJECT_ID);
    sessionStorage.removeItem(QuoteSessionService.PROJECT_NAME);
    sessionStorage.removeItem(QuoteSessionService.QUOTE_ID);
  }

  private setOrRemove(key: string, value: string | null | undefined): void {
    if (value != null && value !== '') {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
    }
  }
}
